import { FunctionComponent } from 'react'
import {
  ArrayLocationAPIResponse,
  SiteRFPPhaseAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@black-bear-energy/rucksack'
import { FilterListRounded } from '@mui/icons-material'
import { SearchBar } from '../common/SearchBar'
import { MenuButton } from '../common/MenuButton'
import { MenuButtonSection } from '../common/MenuButtonSection'
import { FilterMenuItem } from '../common/FilterMenuItem'
import { FilterChip } from '../common/FilterChip'
import { ResetButton } from '../common/ResetButton'
import {
  SiteRFPPhaseOption,
  getSiteRFPPhaseLabel,
} from 'components/helpers/getSiteRFPPhaseLabel'
import { SiteActiveStatus } from '../../api/hooks/useSiteRFPSummaries'

export const SitesSearchBar: FunctionComponent<SitesSearchBarProps> = ({
  search,
  setSearch,
  activeStatus,
  setActiveStatus,
  phase,
  setPhase,
  hidePreRFP,
  arrayLocation,
  setArrayLocation,
}) => {
  const allPhasesOptions = Object.values(SiteRFPPhaseAPIResponse).filter(
    (phase) =>
      phase !== SiteRFPPhaseAPIResponse.DeriskingAndPermits &&
      !(hidePreRFP && phase === SiteRFPPhaseAPIResponse.PreRFP)
  ) as SiteRFPPhaseOption[]
  const allArrayLocations = Object.values(ArrayLocationAPIResponse)

  const filterCount = phase.length + arrayLocation.length
  const filterLabel = filterCount === 0 ? 'filters' : `filters (${filterCount})`
  const areDefaultFilters = filterCount === 0

  const handlePhaseChange = (changedPhase: SiteRFPPhaseOption) => {
    setPhase(
      phase.includes(changedPhase)
        ? phase.filter((existing) => existing !== changedPhase)
        : [...phase, changedPhase]
    )
  }

  const handleArrayLocationChange = (
    changedLocation: ArrayLocationAPIResponse
  ) => {
    setArrayLocation(
      arrayLocation.includes(changedLocation)
        ? arrayLocation.filter((existing) => existing !== changedLocation)
        : [...arrayLocation, changedLocation]
    )
  }

  const resetFilters = () => {
    setPhase([])
    setArrayLocation([])
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          width: '100%',
          rowGap: { xs: 1, sm: 0 },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: '50%' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SearchBar
              label="Search by site or project name"
              search={search}
              setSearch={setSearch}
            />
          </Box>
          <MenuButton
            label={filterLabel}
            icon={<FilterListRounded />}
            reset={{
              handleReset: resetFilters,
              resetDisabled: areDefaultFilters,
            }}
            menuSx={{ maxHeight: 400, overflowY: 'scroll' }}
          >
            <Box component="li" sx={{ py: 1 }}>
              <MenuButtonSection title="Array Location">
                {allArrayLocations.map((location) => (
                  <FilterMenuItem
                    key={location}
                    label={ARRAY_LOCATION_DISPLAY_NAME[location]}
                    checked={arrayLocation.includes(location)}
                    onChange={() => handleArrayLocationChange(location)}
                  />
                ))}
              </MenuButtonSection>
              <MenuButtonSection title="Phase">
                {allPhasesOptions.map((sitePhase) => (
                  <FilterMenuItem
                    key={sitePhase}
                    label={getSiteRFPPhaseLabel(sitePhase)}
                    checked={phase.includes(sitePhase)}
                    onChange={() => handlePhaseChange(sitePhase)}
                  />
                ))}
              </MenuButtonSection>
            </Box>
          </MenuButton>
        </Box>
        <ToggleButtonGroup
          value={activeStatus}
          exclusive
          onChange={(_, newStatus: SiteActiveStatus | null) => {
            if (newStatus !== null) setActiveStatus(newStatus)
          }}
          size="small"
          color="primary"
          sx={{
            display: 'flex',
          }}
        >
          <ToggleButton
            value="active"
            sx={{ padding: '8px 22px', letterSpacing: '0.46px' }}
          >
            Active
          </ToggleButton>
          <ToggleButton
            value="dropped"
            sx={{ padding: '8px 22px', letterSpacing: '0.46px' }}
          >
            Dropped
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {!areDefaultFilters && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1 }}>
          {arrayLocation.map((location) => (
            <FilterChip
              key={location}
              label={ARRAY_LOCATION_DISPLAY_NAME[location]}
              onDelete={() => handleArrayLocationChange(location)}
            />
          ))}
          {arrayLocation.length > 0 && phase.length > 0 && (
            <Divider orientation="vertical" flexItem />
          )}
          {phase.map((sitePhase) => (
            <FilterChip
              key={sitePhase}
              label={getSiteRFPPhaseLabel(sitePhase)}
              onDelete={() => handlePhaseChange(sitePhase)}
            />
          ))}
          <ResetButton onClick={resetFilters} />
        </Box>
      )}
    </Box>
  )
}

SitesSearchBar.displayName = 'SitesSearchBar'

const ARRAY_LOCATION_DISPLAY_NAME: Record<ArrayLocationAPIResponse, string> = {
  [ArrayLocationAPIResponse.Rooftop]: 'Rooftop',
  [ArrayLocationAPIResponse.Carport]: 'Carport',
  [ArrayLocationAPIResponse.Parktop]: 'Parktop',
  [ArrayLocationAPIResponse.Ground]: 'Ground',
  [ArrayLocationAPIResponse.Storage]: 'Storage',
  [ArrayLocationAPIResponse.RoofAndCarport]: 'Roof + Carport',
  [ArrayLocationAPIResponse.RoofAndParktop]: 'Roof + Parktop',
  [ArrayLocationAPIResponse.CarportAndParktop]: 'Carport + Parktop',
  [ArrayLocationAPIResponse.RoofAndCarportAndParktop]:
    'Roof + Carport + Parktop',
}

export interface SitesSearchBarProps {
  search: string
  setSearch: (search: string) => void
  activeStatus: SiteActiveStatus
  setActiveStatus: (activeStatus: SiteActiveStatus) => void
  phase: SiteRFPPhaseOption[]
  setPhase: (phase: SiteRFPPhaseOption[]) => void
  hidePreRFP: boolean
  arrayLocation: ArrayLocationAPIResponse[]
  setArrayLocation: (arrayLocation: ArrayLocationAPIResponse[]) => void
}
